/** 
 * @namespace NavMenu
 * @memberof Promega.Shell
 * @description Methods for controlling the main navigation menu at the top of the site, including showing and hiding navigation flyouts.
 */

import { COLONY as DEFAULT_BREAKPOINT } from './breakpoints';
import isAnimationSupported from '../util/isAnimationSupported';
import trackEvent from '../util/trackEvent';
import { clearModalLoginFields } from './loginRegistration';
import { show as showModal, hide as hideModal, lock as lockModal, unlock as unlockModal, isVisible as isModalVisible } from '../components/modal.js';
import getTestEnvironment from '../util/getTestEnvironment.js';
import * as Search from './search';
import cookie from 'cookie_js';
import getCookie from '../util/cookieCheck.js';
import getQueryStringParameter from '../util/getQueryStringParameter.js';

const CONTROL_BUTTON_CLASS = "site-header__control-button",
    CONTROL_BUTTON_ACTIVE_CLASS = "site-header__control-button--active",
    NAV_BUTTON_CLASS = "site-header__control-button--nav",
    NAV_LINK_CLASS = "site-header__link",
    MOBILE_MENU_CONTROL_CLASS = "mobile-menu-control",
    MOBILE_MENU_CONTROL_ACTIVE_CLASS = "mobile-menu-control--active",
    SITE_HEADER_CLASS = "site-header",
    SITE_HEADER_SECTION_TITLE_CLASS = "site-header__section-title",
    SITE_HEADER_SECTION_TITLE_MOBILE_ACTIVE_CLASS = "site-header__section-title--mobile-active",
    SITE_HEADER_SECTION_CONTAINER_CLASS = "site-header__section-container",
    SITE_HEADER_SECTION_CONTAINER_MOBILE_VISIBLE_CLASS = "site-header__section-container--mobile-visible",
    SITE_HEADER_CLICKEATER_CLASS = "site-header-clickeater",
    SITE_HEADER_CLICKEATER_ACTIVE_CLASS = "site-header-clickeater--active",
    SITE_HEADER_CLICKEATER_ENTER_ANIM_CLASS = "site-header-clickeater--enter-anim",
    SITE_HEADER_CLICKEATER_EXIT_ANIM_CLASS = "site-header-clickeater--exit-anim",
    FLYOUT_CLASS = "site-header__flyout",
    FLYOUT_VISIBLE_CLASS = "site-header__flyout--visible",
    FLYOUT_MOBILE_VISIBLE_CLASS = "site-header__flyout--mobile-visible",
    FLYOUT_ANIMATION_FIRST_ENTRANCE = "site-header__flyout--first-entrance-anim",
    FLYOUT_ANIMATION_TRANS_ENTRANCE = "site-header__flyout--transitional-entrance-anim",
    FLYOUT_ANIMATION_FINAL_EXIT = "site-header__flyout--final-exit-anim",
    FLYOUT_ANIMATION_TRANS_EXIT = "site-header__flyout--transitional-exit-anim",
    BREAKPOINT_COLONY = 1024,
    MOUSEOVER_DURATION = 200,
    FLYOUT_DIMISS_DELAY = 400;

let mouseoverTarget = null,
    mouseoverTimeout = null,
    flyoutDismissTimeout = null,
    menuLocked = false,
    throttleWait;


// Private functions/listeners

// On a touchscreen, tapping the currently open menu icon should close the menu. With a mouse, menus are controlled via hovering.
function handleMenuButtonClickOrTouch(eventTarget, eventType) {
    let menuName = eventTarget.getAttribute("data-promega-nav-flyout");
    if (menuName !== null) {

        // Check if menu is open and that it exists
        let menuIsAlreadyOpen = isMenuOpen(menuName);

        if (menuIsAlreadyOpen !== null) {
            // Check touch vs. mouse behavior
            switch (eventType) {
                case "touchstart":
                    if (menuIsAlreadyOpen) {
                        hideMenu(menuName, { cleanupClickeater: true });
                    } else {
                        showMenu(menuName, { autofocus: true });
                        trackEvent("Components", "Main navigation - opened menu", "Touch");
                    }
                    break;
                case "mouseover":
                    if (!menuIsAlreadyOpen && !menuLocked) {
                        showMenu(menuName);
                    }
                    break;
                case "click":
                    if (["search", "cart", "account"].includes(menuName)) {
                        if (menuIsAlreadyOpen && menuLocked) {
                            hideActiveMenu();
                        } else {
                            if (menuIsAlreadyOpen) {
                                const FLYOUT_MENU = document.querySelector(`.${FLYOUT_CLASS}--${menuName}`);
                                const FLYOUT_MENU_FIRST_INPUT = FLYOUT_MENU.querySelector("input");
                                if (FLYOUT_MENU_FIRST_INPUT) {
                                    FLYOUT_MENU_FIRST_INPUT.focus();
                                    lockMenu();
                                }
                            } else {
                                showMenu(menuName, { autofocus: true });
                            }
                        }
                    }
                    break;
            }
        }
    }
}

function handleInputFocus(event) {
    let eventTarget = event.target;
    if (eventTarget.attributes.getNamedItem("role").value === "combobox") {
        if (!isMenuClickeaterActive()) {
            activateMenuClickeater();
        }
        //if (SITE_HEADER_CONTENT_MAGIC_BOX_INPUT) {
        //    SITE_HEADER_CONTENT_MAGIC_BOX_INPUT.style.borderBottomLeftRadius = "0px";
        //}
        // check if one of the flyouts are open
        let openMenu = document.getElementsByClassName(FLYOUT_VISIBLE_CLASS)[0];
        if (openMenu) {
            // close if it is, but keep the clickeater
            hideMenu(openMenu.getAttribute("data-promega-nav-flyout"), { cleanupClickeater: false, noMenuAnimation: false });
        }

        lockMenu();
    }
}


function throttle(callback, time) {
    // if the variable is true, don't run the function
    if (throttleWait) return;

    // set the wait variable to true to pause the function
    throttleWait = true;

    // use setTimeout to run the function within the specified time
    setTimeout(() => {
        callback();

        // set throttleWait to false once the timer is up to restart the throttle function
        throttleWait = false;
    }, time);
}

function positionSearchbox() {
    // Don't run if under breakpoint of 1024, also clear existing width value
    if (window.innerWidth <= BREAKPOINT_COLONY) {
        document.querySelector(".site-header__search-container").removeAttribute("style");
        return;
    }
    const HEADER_PLACEHOLDER_ELEMENT = document.querySelector(".site-header__searchbox-placeholder");
    // get total width of the space in the header which we want the searchbox to sit in.
    const HEADER_PLACEHOLDER_WIDTH = HEADER_PLACEHOLDER_ELEMENT.offsetWidth;
    // calculate 90% of the space, this will leave a gap either side of the searchbox when placed.
    const SEARCHBOX_WIDTH = Math.floor((HEADER_PLACEHOLDER_WIDTH / 100) * 90);
    const SEARCHBOX_ELEMENT = document.querySelector(".site-header__search-container");
    // set searchbox width
    SEARCHBOX_ELEMENT.style.width = SEARCHBOX_WIDTH + "px";
    // set the left property of searchbox to be 5% of the width of the placeholder + the placeholder's left edge
    const SEARCHBOX_LEFT_VALUE = Math.floor((HEADER_PLACEHOLDER_WIDTH / 100) * 5 + getLeftPosition(HEADER_PLACEHOLDER_ELEMENT));
    SEARCHBOX_ELEMENT.style.left = SEARCHBOX_LEFT_VALUE + "px";
}

function getLeftPosition(element) {
    const RECT = element.getBoundingClientRect();
    return RECT.left + window.pageXOffset;
}

function checkForAlertBanners() {
    let result = { isVisible: false };
    const ALERT_BANNERS = document.querySelectorAll(".alert-container");
    for (let i = 0, j = ALERT_BANNERS.length; i < j; i++) {
        if (window.getComputedStyle(ALERT_BANNERS[i]).display !== "none") {
            return { isVisible: true, element: ALERT_BANNERS[i] };
        }
    }
    return result;
}


function getOffsetAmount() {
    let offsetAmount = 0;
    const ALERT_BANNERS = document.querySelectorAll(".alert-container");
    for (let i = 0, j = ALERT_BANNERS.length; i < j; i++) {
        let currentBanner = ALERT_BANNERS[i];
        if (window.getComputedStyle(currentBanner, null).display !== "none") {
            offsetAmount += currentBanner.offsetHeight;
        }
    }
    return offsetAmount;
}

function menuButtonTouchstart(event) {
    if (event.cancelable) {
        event.preventDefault();
    }
    handleMenuButtonClickOrTouch(event.currentTarget, event.type);
}

function menuButtonMouseover(event) {
    if (getActiveMenu() == null) {
        mouseoverTarget = event.currentTarget;
        mouseoverTarget.addEventListener("mouseout", menuButtonMouseout);
        mouseoverTimeout = setTimeout(menuButtonMouseoverTimedOut, MOUSEOVER_DURATION);
    }
    else handleMenuButtonClickOrTouch(event.currentTarget, event.type);
}

function menuButtonMouseout(event) {
    clearTimeout(mouseoverTimeout);
    cleanupMenuButtonMouseoverTimeout();
}

function menuButtonMouseoverTimedOut(event) {
    handleMenuButtonClickOrTouch(mouseoverTarget, "mouseover");
    cleanupMenuButtonMouseoverTimeout();
}

function cleanupMenuButtonMouseoverTimeout() {
    mouseoverTarget.removeEventListener("mouseout", menuButtonMouseout);
    mouseoverTarget = null;
    mouseoverTimeout = null;
}

// Automatically lock the menu if an input within it receives focus. 
// This prevents it from being dismissed if the user clicks an input and then moves their mouse out of the way.
function flyoutFocusin(event) {
    const FIRST_INPUT = event.currentTarget.querySelector("input");
    if (document.activeElement == FIRST_INPUT) {
        lockMenu();
    }
}

function pageMouseout(event) {
    let relatedTarget = event.relatedTarget;
    if (!relatedTarget || relatedTarget.nodeName === "HTML") {
        if (!menuLocked) {
            hideActiveMenu();
        }
    }
}

function menuButtonClick(event) {
    handleMenuButtonClickOrTouch(event.currentTarget, event.type);
}

// On mobile, the nav links (Products, Applications, Custom Solutions, Resources, Support) are collapsed into a single hamburger menu with accordion functionality.
// Expand/collapse one of these top-level links
function mobileMenuButtonClicked(event) {
    if (event.target.classList.contains(MOBILE_MENU_CONTROL_CLASS)) {
        const MENU_NAME = event.target.getAttribute("data-promega-nav-flyout");
        if (event.target.classList.contains(MOBILE_MENU_CONTROL_ACTIVE_CLASS)) {
            // Collapse the menu
            collapseMobileMenu(MENU_NAME);
            trackEvent("Components", "Main navigation - collapsed mobile menu", event.target.innerText);
        }
        else {
            // Expand the menu
            expandMobileMenu(MENU_NAME);
            trackEvent("Components", "Main navigation - expanded mobile menu", event.target.innerText);
        }
    }
}

// Expand/collapse a heading section within one of the top-level nav links
function navSectionTitleClicked(event) {
    if (event.target.classList.contains(SITE_HEADER_SECTION_TITLE_CLASS)) {
        if (event.target.classList.contains(SITE_HEADER_SECTION_TITLE_MOBILE_ACTIVE_CLASS)) {
            collapseMobileSubmenu(event.target);
            trackEvent("Components", "Main navigation - collapsed mobile submenu", event.target.innerText);
        }
        else {
            expandMobileSubmenu(event.target);
            trackEvent("Components", "Main navigation - expanded mobile submenu", event.target.innerText);
        }
    }
}

// Generates a backdrop that dims the page and provides a surface for touch and mouse users alike to click/hover over to dismiss the menu.
function activateMenuClickeater() {
    const MENU_BACKDROP = document.createElement("div");
    MENU_BACKDROP.className = `${SITE_HEADER_CLICKEATER_CLASS} ${SITE_HEADER_CLICKEATER_ACTIVE_CLASS}`;
    window.addEventListener("mouseout", pageMouseout);

    let eventListeners = {
        "animationend": clickeaterAnimationEnded,
        "touchstart": menuClickEaterClicked,
        "mouseover": menuClickEaterClicked,
        "click": menuClickEaterClicked
    };

    MENU_BACKDROP.classList.add(SITE_HEADER_CLICKEATER_ENTER_ANIM_CLASS);

    Object.entries(eventListeners).forEach(([event, listener]) => {
        MENU_BACKDROP.addEventListener(event, listener);
    });

    document.querySelector(".site-header__flyout-container").insertAdjacentElement("beforebegin", MENU_BACKDROP);
    if (!isAnimationSupported()) clickeaterAnimationEnded();
}

// Removes the backdrop
function deactivateMenuClickeater() {
    let menuBackdrop = document.getElementsByClassName(SITE_HEADER_CLICKEATER_CLASS)[0];
    if (menuBackdrop) {
        menuBackdrop.classList.remove(SITE_HEADER_CLICKEATER_ACTIVE_CLASS);
        window.removeEventListener("mouseout", pageMouseout);
        menuBackdrop.classList.add(SITE_HEADER_CLICKEATER_EXIT_ANIM_CLASS);
        if (!isAnimationSupported()) clickeaterAnimationEnded();
    }
}

function isMenuClickeaterActive() {
    let menuBackdrop = document.getElementsByClassName(SITE_HEADER_CLICKEATER_CLASS)[0];
    return menuBackdrop && menuBackdrop.classList.contains(SITE_HEADER_CLICKEATER_ACTIVE_CLASS);
}

function clickeaterAnimationEnded() {
    let menuBackdrop = document.getElementsByClassName(SITE_HEADER_CLICKEATER_CLASS)[0];
    if (menuBackdrop.classList.contains(SITE_HEADER_CLICKEATER_ENTER_ANIM_CLASS))
        menuBackdrop.classList.remove(SITE_HEADER_CLICKEATER_ENTER_ANIM_CLASS);
    if (menuBackdrop.classList.contains(SITE_HEADER_CLICKEATER_EXIT_ANIM_CLASS))
        menuBackdrop.parentElement.removeChild(menuBackdrop);
}

function menuClickEaterClicked(event) {
    let eventType = event.type;
    if (eventType == "mouseover" && menuLocked) {
        // Ignore the event -- moused over clickeater when search box had focus
    } else {
        if (event.type === "touchstart") {
            event.preventDefault();
        }
        let currentlyOpenFlyout = document.getElementsByClassName(FLYOUT_VISIBLE_CLASS)[0];
        if (currentlyOpenFlyout) {
            let menuName = currentlyOpenFlyout.getAttribute("data-promega-nav-flyout");
            if (event.type === "mouseover") {
                setFlyoutDismissTimeout();
            } else {
                hideMenu(menuName, { cleanupClickeater: true });
            }
            // Special case for always out searchbar
        } else {
            if (isMenuClickeaterActive()) {
                deactivateMenuClickeater();
            }
            let searchBox = Search.getSearchBox();
            if (searchBox) searchBox.blur();
        }

    }
}

function handleSearchBoxFocus() {
    let searchboxElement = document.querySelector('div.magic-box-input input');
    let searchboxParentElement = document.querySelector("div.magic-box-input");
    let dropdownElement = document.querySelector('div.magic-box-suggestions');

    // Check if a MutationObserver is already active
    if (!searchboxElement.hasAttribute('data-observer')) {
        // Function to handle mutations on the dropdown list element
        function handleDropdownMutation(mutationsList, observer) {
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    if (dropdownElement.classList.contains('magic-box-hasSuggestion')) {
                        searchboxParentElement.classList.add('straight-corner');
                        searchboxParentElement.classList.remove('rounded-corner');
                    }
                }
            }
        }

        // Create the MutationObserver
        let dropdownObserver = new MutationObserver(handleDropdownMutation);

        // Options for the MutationObserver
        let observerOptions = {
            attributes: true, // Listen for attribute changes
            attributeFilter: ['class'], // Only listen for changes in the class attribute
        };

        // Start observing the dropdown list element for mutations
        dropdownObserver.observe(dropdownElement, observerOptions);

        // Set a custom attribute to mark the active MutationObserver
        searchboxElement.setAttribute('data-observer', 'active');
    }
}

// Define the on blur event handler function
function handleSearchBoxBlur() {
    let searchboxParentElement = document.querySelector('div.magic-box-input');
    searchboxParentElement.classList.remove('straight-corner');
    searchboxParentElement.classList.add('rounded-corner');
}


function setFlyoutDismissTimeout() {
    flyoutDismissTimeout = setTimeout(flyoutDismissTimedOut, FLYOUT_DIMISS_DELAY);
    document.getElementsByClassName(SITE_HEADER_CLICKEATER_CLASS)[0].addEventListener("mouseout", cleanupFlyoutDismissTimeout);
}

function flyoutDismissTimedOut(event) {
    hideActiveMenu();
    cleanupFlyoutDismissTimeout();
}

function cleanupFlyoutDismissTimeout() {
    clearTimeout(flyoutDismissTimeout);
    flyoutDismissTimeout = null;
    const headerClickeater = document.getElementsByClassName(SITE_HEADER_CLICKEATER_CLASS)[0];
    if (headerClickeater) {
        headerClickeater.addEventListener("mouseout", cleanupFlyoutDismissTimeout);
    }
}

function flyoutAnimationEnded(event) {
    if (typeof event.target.classList !== "undefined" && event.target.classList.contains(FLYOUT_CLASS)) {
        event.target.classList.remove(FLYOUT_ANIMATION_TRANS_EXIT);
        event.target.classList.remove(FLYOUT_ANIMATION_FINAL_EXIT);
        event.target.classList.remove(FLYOUT_ANIMATION_FIRST_ENTRANCE);
        event.target.classList.remove(FLYOUT_ANIMATION_TRANS_ENTRANCE);
    }
}

function howEqualAreArrays(arr1, arr2) {
    let level = 0;
    for (let i = 0; i < arr1.length && i < arr2.length; i++) {
        if (arr1[i] === arr2[i]) level++;
    }
    return level;
}

function splitUrlPath(url) {
    return url.split("/").filter(value => value !== "");
}

function expandDefaultSection() {
    // Pre-expand the mobile nav to show the current site location
    const ALL_NAV_LINKS = document.getElementsByClassName(NAV_LINK_CLASS),
        ALL_TOP_NAV_LINKS = document.getElementsByClassName(NAV_BUTTON_CLASS),
        URL_PATH_PARTS = splitUrlPath(location.pathname);

    // Default to the "Products" top menu, and no submenu link
    let topLinkToExpand = ALL_TOP_NAV_LINKS[0],
        bestNavLink = null,
        bestEquality = 0;

    // Search for a better matching top menu link and replace the guess if found
    for (let i = 0; i < ALL_TOP_NAV_LINKS.length; i++) {
        if (ALL_TOP_NAV_LINKS[i].pathname !== "") {
            if (splitUrlPath(ALL_TOP_NAV_LINKS[i].pathname)[0] === URL_PATH_PARTS[0]) {
                topLinkToExpand = ALL_TOP_NAV_LINKS[i];
                break;
            }
        }
    }

    // Search for a matching submenu link
    for (let i = 0; i < ALL_NAV_LINKS.length; i++) {
        if (ALL_NAV_LINKS[i].getAttribute("href")) {
            let levelOfEquality = howEqualAreArrays(URL_PATH_PARTS, splitUrlPath(ALL_NAV_LINKS[i].pathname));
            if (levelOfEquality > bestEquality) {
                bestEquality = levelOfEquality;
                bestNavLink = ALL_NAV_LINKS[i];
            }
        }
    }

    if (topLinkToExpand) {
        expandMobileMenu(topLinkToExpand.getAttribute("data-promega-nav-flyout"));
    }

    // If we found a nav link that matches more than 1 of the path parts, expand that section
    if (bestNavLink && bestEquality > 1) {
        expandMobileSubmenu(bestNavLink.closest(`.${SITE_HEADER_SECTION_CONTAINER_CLASS}`).previousElementSibling);
    }
}

// API

/**
 * @function showMenu
 * @memberof Promega.Shell.NavMenu
 * @description Shows the nav menu with the given menu name.
 * @param {String} menuName The name identifier of the menu to show
 * @param {Object} [options]
 * @param {Boolean} options.autofocus If true, requests focus on the first available <input> element in the given menu (provided there is one) and locks the menu.
 */
export function showMenu(menuName, options) {
    let currentlyActiveMenu = document.getElementsByClassName(FLYOUT_VISIBLE_CLASS)[0],
        flyoutToShow = document.getElementsByClassName(`${FLYOUT_CLASS}--${menuName}`)[0];

    // Check that the menu exists before attempting to show it
    if (flyoutToShow) {
        // If another menu is currently active, hide that menu before showing the new one
        if (currentlyActiveMenu) {
            hideMenu(currentlyActiveMenu.getAttribute("data-promega-nav-flyout"));
            if (isAnimationSupported()) flyoutToShow.classList.add(FLYOUT_ANIMATION_TRANS_ENTRANCE);
        }
        else if (isAnimationSupported()) flyoutToShow.classList.add(FLYOUT_ANIMATION_FIRST_ENTRANCE);

        // Activate the menu click eater if it's not already active
        if (!isMenuClickeaterActive()) activateMenuClickeater();

        flyoutToShow.classList.add(FLYOUT_VISIBLE_CLASS);
        document.getElementsByClassName(`${CONTROL_BUTTON_CLASS}--${menuName}`)[0].classList.add(CONTROL_BUTTON_ACTIVE_CLASS);

        if (options && options.autofocus) {
            const FIRST_INPUT = flyoutToShow.querySelector("input");
            if (FIRST_INPUT) {
                FIRST_INPUT.focus();
                lockMenu();
            }
        }
    }
}

/**
 * @function hideMenu
 * @memberof Promega.Shell.NavMenu
 * @description Hides the nav menu with the given menu name.
 * @param {String} menuName The name identifier of the menu to hide
 * @param {Object} [options]
 * @param {Boolean} options.cleanupClickeater If true, fully hides the menu and cleans up some helper elements such as the page dimmer.
 * @param {Boolean} options.noMenuAnimation If true, hides the menu without playing an animation. Useful when the menu is being hidden due to a screen resize or orientation change.
 */
function hideMenu(menuName, options) {
    let flyoutToHide = document.getElementsByClassName(`${FLYOUT_CLASS}--${menuName}`)[0];
    const { cleanupClickeater, noMenuAnimation } = options || {};
    const ANIMATION_IS_SUPPORTED = isAnimationSupported();
    if (flyoutToHide) {
        unlockMenu();
        flyoutToHide.classList.remove(FLYOUT_VISIBLE_CLASS);
        let activeButton = document.getElementsByClassName(CONTROL_BUTTON_ACTIVE_CLASS)[0];
        if (activeButton) {
            activeButton.classList.remove(CONTROL_BUTTON_ACTIVE_CLASS);
        }
        if (cleanupClickeater) {
            deactivateMenuClickeater();
            if (ANIMATION_IS_SUPPORTED && !noMenuAnimation) flyoutToHide.classList.add(FLYOUT_ANIMATION_FINAL_EXIT);
        }
        else {
            if (ANIMATION_IS_SUPPORTED && !noMenuAnimation) flyoutToHide.classList.add(FLYOUT_ANIMATION_TRANS_EXIT);
        }

        let searchBox = Search.getSearchBox();
        if (menuName === "search" && searchBox) searchBox.blur();
        if (menuName === "account") clearModalLoginFields();
    }
}

/**
 * @function lockMenu
 * @memberof Promega.Shell.NavMenu
 * @description Locks the menu in its current display state. When the menu is locked, hover events will not trigger actions and an explicit click on the backdrop is required to dismiss the menu (if open).
 * @see Promega.Shell.NavMenu.unlockMenu
 */
export function lockMenu() {
    menuLocked = true;
}

/**
 * @function unlockMenu
 * @memberof Promega.Shell.NavMenu
 * @description Unlocks the menu.
 * @see Promega.Shell.NavMenu.lockMenu
 */
export function unlockMenu() {
    menuLocked = false;
}

/**
 * @function isMenuLocked
 * @memberof Promega.Shell.NavMenu
 * @description Returns a value indicating whether or not the menu is currently locked.
 * @returns {Boolean} True if the menu is locked, otherwise false.
 */
export function isMenuLocked() {
    return menuLocked;
}

/**
 * @function expandMobileMenu
 * @memberof Promega.Shell.NavMenu
 * @description Expands the mobile menu with the given name. Possible values correspond to the names of the top-level navigation menus, and the effect is only visible on small screen sizes.
 * @param {String} menuName The name of the mobile menu to expand. Must match the value of a nav flyout's <code>data-promega-nav-flyout</code> attribute, such as "products" or "resources".
 */
export function expandMobileMenu(menuName) {
    // Expand the menu
    const FLYOUT_TO_TOGGLE = document.getElementsByClassName(`${FLYOUT_CLASS}--${menuName}`)[0],
        menuControl = document.querySelector(`.${MOBILE_MENU_CONTROL_CLASS}[data-promega-nav-flyout="${menuName}"]`);
    menuControl.classList.add(MOBILE_MENU_CONTROL_ACTIVE_CLASS);
    FLYOUT_TO_TOGGLE.classList.add(FLYOUT_MOBILE_VISIBLE_CLASS);
}

/**
 * @function collapseMobileMenu
 * @memberof Promega.Shell.NavMenu
 * @description Collapses the mobile menu with the given name. Possible values correspond to the names of the top-level navigation menus, and the effect is only visible on small screen sizes.
 * @param {String} menuName The name of the mobile menu to collapse
 */
export function collapseMobileMenu(menuName) {
    // Collapse the menu
    const FLYOUT_TO_TOGGLE = document.getElementsByClassName(`${FLYOUT_CLASS}--${menuName}`)[0],
        menuControl = document.querySelector(`.${MOBILE_MENU_CONTROL_CLASS}[data-promega-nav-flyout="${menuName}"]`);
    menuControl.classList.remove(MOBILE_MENU_CONTROL_ACTIVE_CLASS);
    FLYOUT_TO_TOGGLE.classList.remove(FLYOUT_MOBILE_VISIBLE_CLASS);
}

/**
 * @function expandMobileSubmenu
 * @memberof Promega.Shell.NavMenu
 * @description Expands the mobile submenu for the given heading element.
 * @param {Element} sectionHeading The heading element for the category that is to be expanded.
 */
export function expandMobileSubmenu(sectionHeading) {
    if (sectionHeading && sectionHeading.classList && sectionHeading.classList.contains(SITE_HEADER_SECTION_TITLE_CLASS)) {
        const SECTION_TO_TOGGLE = sectionHeading.nextElementSibling;
        sectionHeading.classList.add(SITE_HEADER_SECTION_TITLE_MOBILE_ACTIVE_CLASS);
        SECTION_TO_TOGGLE.classList.add(SITE_HEADER_SECTION_CONTAINER_MOBILE_VISIBLE_CLASS);
    }
}

/**
 * @function collapseMobileSubmenu
 * @memberof Promega.Shell.NavMenu
 * @description Collapses the mobile submenu for the given heading element.
 * @param {Element} sectionHeading The heading element for the category that is to be collapsed.
 */
export function collapseMobileSubmenu(sectionHeading) {
    if (sectionHeading && sectionHeading.classList && sectionHeading.classList.contains(SITE_HEADER_SECTION_TITLE_CLASS)) {
        const SECTION_TO_TOGGLE = sectionHeading.nextElementSibling;
        sectionHeading.classList.remove(SITE_HEADER_SECTION_TITLE_MOBILE_ACTIVE_CLASS);
        SECTION_TO_TOGGLE.classList.remove(SITE_HEADER_SECTION_CONTAINER_MOBILE_VISIBLE_CLASS);
    }
}

/**
 * @function hideActiveMenu
 * @memberof Promega.Shell.NavMenu
 * @description Hides the nav menu that is currently visible, if there is one.
 */
export function hideActiveMenu() {
    let currentlyActiveMenu = document.getElementsByClassName(FLYOUT_VISIBLE_CLASS)[0];
    if (currentlyActiveMenu) hideMenu(currentlyActiveMenu.getAttribute("data-promega-nav-flyout"), { cleanupClickeater: true, noMenuAnimation: true });
}

/**
 * @function getActiveMenu
 * @memberof Promega.Shell.NavMenu
 * @description Gets the name of the menu that is currently active.
 * @returns {String} The name of the active menu. Null if no menu is active.
 */
export function getActiveMenu() {
    let currentlyActiveMenu = document.getElementsByClassName(FLYOUT_VISIBLE_CLASS)[0];
    if (currentlyActiveMenu) return currentlyActiveMenu.getAttribute("data-promega-nav-flyout");
    return null;
}

/**
 * @function isMenuOpen
 * @memberof Promega.Shell.NavMenu
 * @description Returns a value indicating whether or not the menu with the given name is currently active.
 * @param {String} menuName
 * @returns {Boolean}
 */
export function isMenuOpen(menuName) {
    // Return true if the menu is open, false if it is closed, or null if the menu does not exist
    let menuEl = document.getElementsByClassName(`${FLYOUT_CLASS}--${menuName}`)[0];
    if (menuEl) return menuEl.classList.contains(FLYOUT_VISIBLE_CLASS);
    else return null;
}

/**
 * @function setAccountDisplayName
 * @memberof Promega.Shell.NavMenu
 * @param {String} name A name to be displayed in the Account menu
 */
export function setAccountDisplayName(name) {
    const NAME_CONTAINER = document.querySelector(".site-header__account-full-name");
    if (NAME_CONTAINER)
        NAME_CONTAINER.innerText = name;
    else
        throw new Error("Main Nav doesn't include a container for the account name. Are you logged in?");
}

/** 
 *  @namespace CartFlyout
 *  @memberof Promega.Shell.NavMenu
 *  @description A collection of functions to control the functionality of the cart flyout.
 */
export const CartFlyout = {

    /**
     * @function showQuantity
     * @memberof Promega.Shell.NavMenu.CartFlyout
     * @description Shows the quantity icon on the cart button in the nav.
     */
    showQuantity: () => {
        let cartButton = document.getElementsByClassName(`${CONTROL_BUTTON_CLASS}--cart`)[0];
        if (cartButton) cartButton.classList.add(`${CONTROL_BUTTON_CLASS}--cart-with-quantity`);
    },

    /**
     * @function hideQuantity
     * @memberof Promega.Shell.NavMenu.CartFlyout
     * @description Hides the quantity icon on the cart button in the nav.
    */
    hideQuantity: () => {
        let cartButton = document.getElementsByClassName(`${CONTROL_BUTTON_CLASS}--cart`)[0];
        if (cartButton) cartButton.classList.remove(`${CONTROL_BUTTON_CLASS}--cart-with-quantity`);
    },

    /**
     * @function refreshQuantity
     * @memberof Promega.Shell.NavMenu.CartFlyout
     * @description Updates the quantity icon with the current cart quantity stored in the user's cookies, showing or hiding the icon as necessary.
    */
    refreshQuantity: () => {
        let cartButton = document.getElementsByClassName(`${CONTROL_BUTTON_CLASS}--cart`)[0];
        // Check that the cart button exists before attempting to set, since branches without Commerce (such as China) do not have a cart
        if (cartButton) {
            let cartQuantity = parseInt(cookie.get("BasketQuantity"), 10);
            if (!isNaN(cartQuantity)) {
                cartQuantity = 0 > cartQuantity ? 0 : cartQuantity;
                let shortQuantity = cartQuantity > 99 ? "99+" : cartQuantity;
                cartButton.setAttribute("data-cart-quantity", shortQuantity);

                if (cartQuantity > 0) CartFlyout.showQuantity();
                else CartFlyout.hideQuantity();

                let allCartQuantities = document.getElementsByClassName("promega-cart-quantity"),
                    totalQuantities = allCartQuantities.length;
                for (let i = 0; i < totalQuantities; i++) {
                    allCartQuantities[i].innerText = cartQuantity;
                }
            }
            else CartFlyout.hideQuantity();
        }
    }
}

// Init code
if ((typeof document !== "undefined") && document.querySelector(`.${SITE_HEADER_CLASS}`) && !window.HeaderHack) {
    window.HeaderHack = true;

    // Absolute hack for dealing with banners on IE11
    if (checkForAlertBanners().isVisible && !!window.MSInputMethodContext && !!document.documentMode) {
        document.querySelector(".site-header").classList.add("header-relative");
    }

    // Function to handle mutations
    function handleSearchBarMutation(mutationsList, observer) {
        for (const mutation of mutationsList) {
            if (mutation.type === 'childList') {
                const SEARCHBOX_ELEMENT = document.querySelector('div.magic-box-input input');
                if (SEARCHBOX_ELEMENT) {
                    // Search bar element is present, add the necessary event listeners and mutation observer
                    document.querySelector("div.magic-box-input").classList.add("rounded-corner");
                    SEARCHBOX_ELEMENT.addEventListener('focus', handleSearchBoxFocus);
                    SEARCHBOX_ELEMENT.addEventListener('blur', handleSearchBoxBlur);
                    observer.disconnect(); // Stop observing once the search bar element is found
                }
            }
        }
    }

    // Create the MutationObserver
    const OBSERVER = new MutationObserver(handleSearchBarMutation);

    // Options for the MutationObserver
    const OBSERVER_OPTIONS = {
        childList: true, // Listen for changes in the child nodes
        subtree: true, // Observe changes in the entire subtree
    };

    // Start observing the container element for mutations
    const containerElement = document.querySelector('header.site-header');
    OBSERVER.observe(containerElement, OBSERVER_OPTIONS);


    let menuButtons = document.getElementsByClassName(CONTROL_BUTTON_CLASS);
    for (let i = 0; i < menuButtons.length; i++) {
        menuButtons[i].addEventListener("touchstart", menuButtonTouchstart);
        menuButtons[i].addEventListener("mouseover", menuButtonMouseover);
        menuButtons[i].addEventListener("click", menuButtonClick);
    }

    const NAV_FLYOUTS = document.getElementsByClassName(FLYOUT_CLASS);
    for (let i = 0; i < NAV_FLYOUTS.length; i++) {
        NAV_FLYOUTS[i].addEventListener("focusin", flyoutFocusin);
    }

    const SEARCHBOX_PLACEHOLDER = document.querySelector(".site-header__search-container");
    if (SEARCHBOX_PLACEHOLDER) {
        SEARCHBOX_PLACEHOLDER.addEventListener("focusin", handleInputFocus);
        //SEARCHBOX_PLACEHOLDER.addEventListener("focusout", handleInputLoseFocus);
    }

    document.addEventListener("animationend", flyoutAnimationEnded);
    document.addEventListener("click", mobileMenuButtonClicked);
    document.addEventListener("click", navSectionTitleClicked);
    window.addEventListener("resize", () => { positionSearchbox() });


    CartFlyout.refreshQuantity();
    DEFAULT_BREAKPOINT.query.addListener(() => hideActiveMenu());

    expandDefaultSection();

    const loginAttempt = getQueryStringParameter(location.href, "loginAttempt");
    if (loginAttempt === "true") {
        const COMMERCE_COOKIE = getCookie(".COMMERCE_AUTH");
        const PAGE_URL = new URL(window.location.href);
        const SEARCH_PARAMS = new URLSearchParams(PAGE_URL.search);

        //clear loginAttempt param
        SEARCH_PARAMS.delete("loginAttempt");
        const symbol = SEARCH_PARAMS.toString().length > 0 ? "?" : "";
        window.history.replaceState({}, "", `${location.pathname}${symbol}${SEARCH_PARAMS}${location.hash}`);

        //show alternate link login modal
        if (!COMMERCE_COOKIE) {
            const REDIRECT_MODAL = document.querySelector(".site-header__login-redirect-modal");
            if (REDIRECT_MODAL) {
                REDIRECT_MODAL.querySelector("a").setAttribute("href", `https://${getTestEnvironment() || "www"}.promega.com/commerce/login`);
                showModal(document.querySelector(".site-header__login-redirect-modal")).then(() => {
                    REDIRECT_MODAL.querySelector("header").focus();
                });
            }
        }
    }
    // Run once on page to position correctly for initial load.
    positionSearchbox();
}
